import type { FileSystem, Folder, Prompt } from '../types';
import { addFolderToFS, addPromptToFolderFS, removePromptFromFS } from './fileSystemUtils';

const TRASH_FOLDER_ID = 'trash';

const timeOf = (value: string | null | undefined) => {
  const t = value ? Date.parse(value) : NaN;
  return Number.isNaN(t) ? 0 : t;
};

type Located = { prompt: Prompt; folderId: string };

const indexPrompts = (fs: FileSystem): Map<string, Located> => {
  const map = new Map<string, Located>();
  const walk = (folders: Folder[]) => {
    for (const folder of folders || []) {
      for (const prompt of folder.prompts || []) map.set(prompt.id, { prompt, folderId: folder.id });
      walk(folder.subFolders || []);
    }
  };
  walk(fs);
  return map;
};

const replacePromptInPlace = (fs: FileSystem, prompt: Prompt): FileSystem =>
  fs.map(folder => ({
    ...folder,
    prompts: (folder.prompts || []).map(p => (p.id === prompt.id ? prompt : p)),
    subFolders: replacePromptInPlace(folder.subFolders || [], prompt),
  }));

/** Neuere Fassung je ID gewinnt; bei Gleichstand die lokale, damit ungespeicherte Änderungen bleiben. */
const pickLocal = (local: Prompt, server: Prompt) => timeOf(server.updatedAt) <= timeOf(local.updatedAt);

/**
 * Führt nach einem 409 (Server wurde zwischenzeitlich geändert, z. B. durch den Skill-Sync) beide Stände zusammen.
 * Basis ist der Server; lokale Einträge, die neuer oder nur lokal vorhanden sind, werden übernommen,
 * samt lokalem Ablageort. Lokal endgültig gelöschte Einträge kommen dabei vom Server zurück.
 */
export const mergeFileSystems = (local: FileSystem, server: FileSystem): FileSystem => {
  let result: FileSystem = Array.isArray(server) ? server : [];
  const serverFolderIds = new Set<string>();
  const collectIds = (folders: Folder[]) => {
    for (const folder of folders || []) {
      serverFolderIds.add(folder.id);
      collectIds(folder.subFolders || []);
    }
  };
  collectIds(result);

  // Lokal angelegte Ordner zuerst (Eltern vor Kindern), leer – die Einträge folgen unten
  const addMissingFolders = (folders: Folder[], parentId: string | null) => {
    for (const folder of folders || []) {
      if (!serverFolderIds.has(folder.id)) {
        const shell: Folder = { ...folder, prompts: [], subFolders: [] };
        if (parentId === null) {
          const trashIndex = result.findIndex(f => f.id === TRASH_FOLDER_ID);
          result = addFolderToFS(result, null, shell, trashIndex >= 0 ? trashIndex : undefined);
        } else {
          result = addFolderToFS(result, parentId, shell);
        }
        serverFolderIds.add(folder.id);
      }
      addMissingFolders(folder.subFolders || [], folder.id);
    }
  };
  addMissingFolders(Array.isArray(local) ? local : [], null);

  const serverPrompts = indexPrompts(result);
  for (const [id, { prompt, folderId }] of indexPrompts(Array.isArray(local) ? local : [])) {
    const onServer = serverPrompts.get(id);
    if (!onServer) {
      result = addPromptToFolderFS(result, folderId, prompt);
      continue;
    }
    if (!pickLocal(prompt, onServer.prompt)) continue;
    if (onServer.folderId !== folderId) {
      result = addPromptToFolderFS(removePromptFromFS(result, id), folderId, prompt);
    } else if (JSON.stringify(onServer.prompt) !== JSON.stringify(prompt)) {
      result = replacePromptInPlace(result, prompt);
    }
  }
  return result;
};

export const mergeTemplates = (local: Prompt[], server: Prompt[]): Prompt[] => {
  const merged = new Map<string, Prompt>();
  for (const t of server || []) merged.set(t.id, t);
  for (const t of local || []) {
    const onServer = merged.get(t.id);
    if (!onServer || pickLocal(t, onServer)) merged.set(t.id, t);
  }
  return [...merged.values()];
};
